import React from 'react';
import mergeRefs from 'react-merge-refs';
import Portal from '../shared/portal';
import useStyles from './use_styles';
import UtilityButton from '../shared/utility_button';
import { ModalInterface } from './types';
import modalI18n from './i18n';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';
import {
  FloatingFocusManager,
  FloatingOverlay,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';

const Modal = React.forwardRef<
  HTMLDivElement,
  ModalInterface & Omit<React.HTMLAttributes<HTMLElement>, keyof ModalInterface>
>(
  (
    {
      'aria-describedby': ariaDescribedByProp,
      'aria-labelledby': ariaLabelledByProp,
      align = 'start',
      alignFooter,
      children,
      className,
      closeButton = false,
      closeOnBlur = true,
      customStyle,
      expanded = false,
      footer,
      header,
      i18n,
      id,
      maxWidth,
      onClose,
      portal = false,
      portalContainer,
      show = false,
      title,
      ...modalProps
    },
    ref
  ) => {
    const theme = useTheme();
    const isHc = theme.mode === 'contrast';
    const isPill = theme.shape === 'pill';
    const { refs, context } = useFloating({
      open: show,
      onOpenChange: (isOpen, e) => {
        if (!isOpen) {
          onClose?.(e as unknown as React.SyntheticEvent);
        }
      },
    });
    const { isMounted, styles } = useTransitionStyles(context, {
      initial: {
        opacity: 0,
        transform: 'scale(0.95)',
      },
      open: {
        opacity: 1,
        transform: 'scale(1)',
        transitionProperty: 'opacity, transform',
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-out',
      },
      close: {
        opacity: 0,
        transform: 'scale(0.95)',
        transitionProperty: 'opacity, transform',
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-in',
      },
    });

    const backdropClickRef = React.useRef(false);

    const handleRef = mergeRefs([refs.setFloating, ref]);

    const click = useClick(context, { keyboardHandlers: false });
    const role = useRole(context);
    useDismiss(context, { enabled: closeOnBlur });

    const { getFloatingProps } = useInteractions([click, role]);

    const closeModal = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (onClose && show) onClose(event);
      },
      [show, onClose]
    );

    const bId = useId();
    const tId = useId();
    const bodyId = id || bId;
    const titleId = `${id || tId}-label`;
    const ariaLabelledBy = ariaLabelledByProp || titleId;

    const style = useStyles({ align, alignFooter, maxWidth });

    const renderHeader = React.useMemo(() => {
      if (!title && !header) return undefined;

      return header ? (
        <div
          className="vn-modal--header"
          css={[style.header.base, align && style.align]}
          id={titleId}
        >
          {header}
        </div>
      ) : (
        <h4 className="vn-modal--title" css={style.title} id={titleId}>
          {title}
        </h4>
      );
    }, [align, title, header, style, titleId]);

    const handleOverlayClick = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (backdropClickRef.current && closeOnBlur) closeModal(event);
        backdropClickRef.current = false;
      },
      [closeModal, closeOnBlur]
    );

    return (
      <Portal container={portalContainer} disablePortal={!portal}>
        {(show || isMounted) && (
          <FloatingOverlay
            lockScroll
            css={[style.overlay.base, show && style.overlay.show]}
            // dismiss from floating-ui is not working properly (needs double click)
            onClick={handleOverlayClick}
            role="presentation"
          >
            <FloatingFocusManager context={context}>
              <div
                aria-describedby={ariaDescribedByProp}
                aria-labelledby={ariaLabelledBy}
                aria-hidden={!show}
                aria-modal="true"
                // keeping vn-modal--content for retro-compatibility
                className={className || 'vn-modal--dialog vn-modal--content'}
                css={[
                  style.root.base,
                  expanded && style.root.expanded,
                  isHc && style.root.hc,
                  isPill && style.pill.root,
                  highContrastMediaQuery(theme),
                  customStyle,
                ]}
                style={{ ...styles }}
                ref={handleRef}
                {...getFloatingProps()}
                {...modalProps}
              >
                {renderHeader}
                <div
                  className="vn-modal--body"
                  css={[style.body, align && style.align]}
                  id={bodyId}
                >
                  {children}
                </div>
                {footer && (
                  <div
                    className="vn-modal--footer"
                    css={[
                      style.footer.base,
                      align && style.align,
                      alignFooter && style.footer.alignFooter,
                    ]}
                  >
                    {footer}
                  </div>
                )}
                {closeButton && (
                  <UtilityButton
                    className="vn-modal__close"
                    aria-label={i18n?.close || modalI18n.close}
                    css={[style.closeButton, style.pill.closeButton]}
                    onClick={closeModal}
                  />
                )}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </Portal>
    );
  }
);

Modal.displayName = 'Modal';

export default Modal;
